import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router";
import { Ioc } from "@core/ioc/ioc";
import type { IAuthModel } from "@models/auth/IAuthModel";
import { AUTH_MODEL } from "@models/auth/IAuthModel";

/**
 * App router.
 *
 * Mirrors Unity's `ViewNames.Login` → `ViewNames.Projects` →
 * `ViewNames.LoadProjectProgress` → `ViewNames.Project` flow:
 *
 *   `/login`                — login + reset/confirm/create-account flows
 *   `/projects`             — landing page after sign-in (project grid)
 *   `/projects/:projectId`  — editor for a specific project
 *   `/query?sl&act&prj`     — shared-link hand-off to the editor
 *   `/`                     — alias for `/projects`
 *
 * Guards:
 *   - Routes with `meta.requiresAuth` (default for everything except
 *     `/login`) wait for `auth.initializeAsync` then redirect to `/login`
 *     if the user is not authenticated.
 *   - `/login` redirects to `/projects` once the user is authenticated,
 *     so a refresh on a stale `/login` URL doesn't trap them on the form.
 */

const routes: RouteRecordRaw[] = [
  {
    path: "/login",
    name: "Login",
    component: () => import("@components/panels/auth/LoginView.vue"),
    meta: { requiresAuth: false, layout: "auth" },
  },
  {
    path: "/projects",
    name: "Projects",
    component: () => import("@components/panels/projects/ProjectsView.vue"),
    meta: { requiresAuth: true, layout: "projects" },
  },
  {
    // Browser equivalent of the website launcher handing
    // `c3d://query?sl&act&prj` to Unity. It is protected so the existing
    // `/login?from=<fullPath>` guard is the deferred-link store.
    path: "/query",
    name: "DeepLink",
    component: () => import("@components/panels/sharing/DeepLinkView.vue"),
    meta: { requiresAuth: true, layout: "auth" },
  },
  {
    path: "/projects/:projectId",
    name: "Editor",
    component: () => import("@components/panels/project/ProjectView.vue"),
    props: (route) => ({
      projectId: String(route.params.projectId ?? ""),
      sceneId: typeof route.query.sceneId === "string"
        ? route.query.sceneId
        : undefined,
    }),
    meta: { requiresAuth: true, layout: "editor" },
  },
  {
    path: "/",
    redirect: { name: "Projects" },
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: { name: "Projects" },
  },
];

export const router = createRouter({
  // `import.meta.env.BASE_URL` is Vite's normalized `base` — "/" for the default
  // deployment, "/<channel>/" for a named channel (`scripts/deploy-client-dev.sh`).
  // Without it a channel build parses its own mount path as a route, matches only
  // the catch-all, and rewrites every href to the origin root — walking the user
  // out of the channel and into the DEFAULT build on the very first navigation.
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

router.beforeEach(async (to) => {
  const auth = Ioc.resolve<IAuthModel>(AUTH_MODEL);

  if (!auth.isInitialized) {
    await auth.initializeAsync();
  }

  const requiresAuth = to.meta.requiresAuth !== false;

  if (requiresAuth && !auth.isAuthenticated) {
    return {
      name: "Login",
      query: to.fullPath !== "/" ? { from: to.fullPath } : undefined,
    };
  }

  if (!requiresAuth && auth.isAuthenticated) {
    const from = typeof to.query.from === "string" ? to.query.from : { name: "Projects" };
    return from;
  }

  return true;
});
